import Papa from 'papaparse';

const MANUFACTURING_DATE_REGEX = /^\d{8}$/;
const SERVICE_TAG_FIXED_FORMAT_REGEX = /^(\d{12})\.(\d{8})\.([A-Za-z0-9]+)\.(\d+)$/;

const parseFixedFormatServiceTag = (serviceTag) => {
  if (!serviceTag || typeof serviceTag !== 'string') {
    return null;
  }

  const normalizedTag = serviceTag.trim();
  const match = normalizedTag.match(SERVICE_TAG_FIXED_FORMAT_REGEX);

  if (!match) {
    return null;
  }

  return {
    twelveNC: match[1],
    manufacturingDate: match[2],
    factoryCode: match[3],
    serialNumber: match[4],
  };
};

const extractBatchIdFromServiceTag = (serviceTag) => {
  const parsed = parseFixedFormatServiceTag(serviceTag);
  if (!parsed) return null;
  // Last segment format: <BatchID><SerialNumber>
  // SerialNumber is the last 5 digits; BatchID is everything before that.
  const lastSegment = String(parsed.serialNumber || '');
  return lastSegment.length > 5 ? lastSegment.slice(0, lastSegment.length - 5) : lastSegment;
};

// Extract twelveNC from service tag format: 12nc.manufacturingDate.FactoryCode.BatchId.SerialNum
const extract12NCFromServiceTag = (serviceTag) => {
  const parsed = parseFixedFormatServiceTag(serviceTag);
  return parsed ? parsed.twelveNC : null;
};

// Extract manufacturing date from service tag format: 12nc.manufacturingDate.FactoryCode.BatchId.SerialNum
const extractManufacturingDateFromServiceTag = (serviceTag) => {
  const parsed = parseFixedFormatServiceTag(serviceTag);
  return parsed ? parsed.manufacturingDate : null;
};

// Extract manufacturing date from uploaded file name format: TwelveNC.DDMMYYYY.FactoryCode.SerialNumber(.csv)
const extractManufacturingDateFromUploadedFileName = (fileName) => {
  if (!fileName || typeof fileName !== 'string') {
    return null;
  }

  const normalizedFileName = fileName.trim().replace(/\.(csv|txt)$/i, '');
  const parts = normalizedFileName.split('.');
  const datePart = parts.length > 1 ? String(parts[1]).trim() : '';

  return MANUFACTURING_DATE_REGEX.test(datePart) ? datePart : null;
};

// Process multiple CSV files
export const processMultipleCSVFiles = async (files, isFactoryFileOnly = false) => {
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('No CSV files provided');
  }

  // If single file, use original single-file handler for backward compatibility
  if (files.length === 1) {
    return processCSVFile(files[0], isFactoryFileOnly);
  }

  // Process multiple files
  const allData = [];
  const fileNames = [];

  for (const file of files) {
    try {
      const processedData = await processCSVFile(file, isFactoryFileOnly);
      allData.push({
        ...processedData,
        sourceFileName: file.name
      });
      fileNames.push(file.name);
    } catch (error) {
      console.error(`Error processing file ${file.name}:`, error);
      throw new Error(`Failed to process file "${file.name}": ${error.message}`);
    }
  }

  // Merge all processed data
  return mergeProcessedData(allData, fileNames);
};

const mergeProcessedData = (processedDataArray, fileNames) => {
  const mergedData = {
    twelveNCs: [], // Unique 12NC entries instead of batchIds
    totalServiceTags: 0,
    originalCsvData: [],
    serviceTagColumn: null,
    sourceFileNames: fileNames,
    sourceFileMetadata: [],
    sourceFileManufacturingDates: [],
    twelveNCMap: {} // Maps twelveNC to manufacturing date and other info
  };

  const twelveNCSet = new Set();
  const twelveNCInfoMap = {}; // Maps twelveNC -> {manufacturingDate, count, fromFiles}

  for (const processedData of processedDataArray) {
    mergedData.totalServiceTags += processedData.totalServiceTags;
    mergedData.originalCsvData.push(...(processedData.originalCsvData || []));

    if (!mergedData.serviceTagColumn) {
      mergedData.serviceTagColumn = processedData.serviceTagColumn;
    }

    if (processedData.sourceFileName) {
      mergedData.sourceFileMetadata.push({
        sourceFileName: processedData.sourceFileName,
        manufacturingDate: processedData.sourceFileManufacturingDate || null,
      });

      if (processedData.sourceFileManufacturingDate) {
        mergedData.sourceFileManufacturingDates.push(processedData.sourceFileManufacturingDate);
      }
    }

    // Extract unique 12NCs
    if (Array.isArray(processedData.originalCsvData)) {
      processedData.originalCsvData.forEach(row => {
        const serviceTag = row[processedData.serviceTagColumn];
        if (serviceTag) {
          const twelveNC = extract12NCFromServiceTag(serviceTag);
          const manufacturingDate =
            extractManufacturingDateFromServiceTag(serviceTag) ||
            processedData.sourceFileManufacturingDate ||
            null;

          if (twelveNC && /^\d{12}$/.test(twelveNC)) {
            twelveNCSet.add(twelveNC);

            if (!twelveNCInfoMap[twelveNC]) {
              twelveNCInfoMap[twelveNC] = {
                twelveNC,
                manufacturingDate,
                count: 0,
                fromFiles: new Set()
              };
            }
            twelveNCInfoMap[twelveNC].count++;
            twelveNCInfoMap[twelveNC].fromFiles.add(processedData.sourceFileName);
          }
        }
      });
    }
  }

  mergedData.sourceFileManufacturingDates = Array.from(new Set(mergedData.sourceFileManufacturingDates));

  mergedData.twelveNCs = Array.from(twelveNCSet).sort();
  mergedData.twelveNCMap = twelveNCInfoMap;

  return mergedData;
};

export const processCSVFile = (file, isFactoryFileOnly = false) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const processedData = processCSVData(results.data, isFactoryFileOnly);
          processedData.sourceFileName = file?.name || '';
          processedData.sourceFileManufacturingDate = extractManufacturingDateFromUploadedFileName(file?.name || '');
          resolve(processedData);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      }
    });
  });
};

const processCSVData = (csvData, isFactoryFileOnly = false) => {
  if (!Array.isArray(csvData) || csvData.length === 0) {
    throw new Error('CSV file is empty or invalid');
  }

  const headers = Object.keys(csvData[0]);
  const normalizeHeader = (value) => String(value || '').toLowerCase().replace(/[\s_]/g, '');
  const firstHeader = headers[0] || '';

  // Business rule: if first column is servicetag, all other columns must be empty.
  // Skip data check for factory-file-only format
  if (normalizeHeader(firstHeader) === 'servicetag') {
    const otherHeaders = headers.slice(1);

    // Skip data validation for factory-file-only format; API will validate and return invalid servicetags
    if (!isFactoryFileOnly) {
      for (let rowIndex = 0; rowIndex < csvData.length; rowIndex += 1) {
        const row = csvData[rowIndex] || {};

        for (const header of otherHeaders) {
          const value = row[header];
          const hasValue = typeof value === 'string'
            ? value.trim() !== ''
            : value !== null && value !== undefined && String(value).trim() !== '';

          if (hasValue) {
            throw new Error(
              `Invalid CSV format: when first column is "servicetag", other columns must be empty (found data in column "${header}" at row ${rowIndex + 1}).`
            );
          }
        }
      }
    }
  }

  // Find the column that contains service tags
  // Look for common column names
  const possibleColumns = ['servicetag', 'service_tag', 'service tag', 'serviceTag'];
  let serviceTagColumn = null;

  if (csvData.length > 0) {
    const lowerCaseHeaders = Object.keys(csvData[0]).map(h => h.toLowerCase());

    for (const col of possibleColumns) {
      const foundCol = lowerCaseHeaders.find(h => h.includes(col));
      if (foundCol) {
        serviceTagColumn = Object.keys(csvData[0]).find(k => k.toLowerCase() === foundCol);
        break;
      }
    }

    // If no standard column found, use the first column
    if (!serviceTagColumn) {
      serviceTagColumn = Object.keys(csvData[0])[0];
    }
  }

  if (!serviceTagColumn) {
    throw new Error('No service tag column found in CSV');
  }

  // Enforce fixed service tag format for every populated row:
  // TwelveNC.manufacturingDate(DDMMYYYY).FactoryCode.SerialNumber
  for (let rowIndex = 0; rowIndex < csvData.length; rowIndex += 1) {
    const row = csvData[rowIndex] || {};
    const rawTag = row[serviceTagColumn];
    const serviceTag = String(rawTag || '').trim();

    if (!serviceTag) {
      throw new Error(
        `Invalid service tag format at row ${rowIndex + 2}: value is empty. Expected format: TwelveNC.DDMMYYYY.FactoryCode.SerialNumber`
      );
    }

    const parsedTag = parseFixedFormatServiceTag(serviceTag);
    if (!parsedTag) {
      throw new Error(
        `Invalid service tag format at row ${rowIndex + 2}: "${serviceTag}". Expected format: TwelveNC.DDMMYYYY.FactoryCode.SerialNumber (example: 911401816085.10072025.KY1.2506004800060)`
      );
    }
  }

  // Extract service tags and get unique batch IDs only
  const serviceTags = csvData
    .map(row => row[serviceTagColumn])
    .filter(tag => tag && tag.trim() !== '');

  const uniqueBatchIds = extractUniqueBatchIds(serviceTags);
  const twelveNCSet = new Set();
  const twelveNCMap = {};

  serviceTags.forEach((tag) => {
    const parsedTag = parseFixedFormatServiceTag(tag);
    if (!parsedTag) {
      return;
    }

    twelveNCSet.add(parsedTag.twelveNC);

    if (!twelveNCMap[parsedTag.twelveNC]) {
      twelveNCMap[parsedTag.twelveNC] = {
        twelveNC: parsedTag.twelveNC,
        manufacturingDate: parsedTag.manufacturingDate,
        count: 0,
        fromFiles: new Set(),
      };
    }

    twelveNCMap[parsedTag.twelveNC].count += 1;
  });

  // Store original CSV data with extracted batch IDs, 12NC, and manufacturing date for each row
  const enrichedCsvData = csvData.map(row => {
    const serviceTag = row[serviceTagColumn];
    const parsedTag = parseFixedFormatServiceTag(serviceTag);
    const extractedBatchId = extractBatchIdFromServiceTag(serviceTag);
    const extracted12NC = extract12NCFromServiceTag(serviceTag);
    const extractedManufacturingDate = extractManufacturingDateFromServiceTag(serviceTag);

    const extractedSerialNumber = parsedTag
      ? (String(parsedTag.serialNumber).length > 5 ? String(parsedTag.serialNumber).slice(-5) : String(parsedTag.serialNumber))
      : null;

    return {
      ...row,
      extractedBatchId: extractedBatchId,
      extracted12NC: extracted12NC,
      extractedManufacturingDate: extractedManufacturingDate,
      extractedFactoryCode: parsedTag ? parsedTag.factoryCode : null,
      extractedSerialNumber: extractedSerialNumber,
    };
  });

  // Generate processed data structure with batch IDs and original data
  const processedData = {
    batchIds: uniqueBatchIds,
    twelveNCs: Array.from(twelveNCSet).sort(),
    twelveNCMap,
    totalServiceTags: serviceTags.length,
    originalCsvData: enrichedCsvData,
    serviceTagColumn: serviceTagColumn
  };

  return processedData;
};

const extractUniqueBatchIds = (serviceTags) => {
  const batchIds = new Set();

  serviceTags.forEach(tag => {
    const batchId = extractBatchIdFromServiceTag(tag);
    if (batchId) {
      batchIds.add(batchId);
    }
  });

  return Array.from(batchIds);
};

export const generateBOM = (processedData) => {
  const bom = {};

  // Support both old structure (batchIds) and new structure (twelveNCs)
  const ids = processedData.twelveNCs || processedData.batchIds || [];
  const idType = processedData.twelveNCs ? 'twelveNC' : 'batchId';

  ids.forEach(id => {
    const key = idType === 'twelveNC' ? id : id;
    bom[key] = {
      [idType]: id,
      createdAt: new Date().toISOString(),
      bomFile: null, // Placeholder for uploaded BOM file
      bomMode: 'upload', // Default to upload mode
      bomData: null // For created BOM data
    };

    // Add manufacturing date if available for twelveNC
    if (idType === 'twelveNC' && processedData.twelveNCMap && processedData.twelveNCMap[id]) {
      bom[key].manufacturingDate = processedData.twelveNCMap[id].manufacturingDate;
    }
  });

  return bom;
};